import React, {
  useState, useCallback, useMemo,
} from 'react';
import uuid from 'uuid';
import {
  useAppSelector
} from 'store';
import {
  Grid, Form, Button, Dropdown, Icon, Message,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import util from 'utils/utils';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { FakeLink, CheckboxHeader } from 'components/lib/UI';
import useTheme from 'theme/useTheme';
import { includeSections } from 'components/ideas/Body';

import { Droppable, Draggable } from 'react-beautiful-dnd';
import FormatField from './FormatField';

const StyledContainer = styled.div`
  border-radius: 5px;
  margin-bottom: 20px;
  padding: 20px;
  background-color: ${(props) => {
    if (props.includeSection?.colour) return util.adjustColour(props.includeSection.colour, 0.15);
    if (props.section?.type === 'edi') return 'rgba(250,0,0,0.2)';
    return '#b9b4b414';
  }};
`;

const StyledSection = styled.div`
  padding: 20px;
  border-radius: 15px;
  border-bottom: 0.5px solid #babcbe;
  background-color: #fff;
  margin-bottom: 20px;
`;

const StyledSectionHeader = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (!theme.sizes.isComputer ? 'column-reverse' : 'row')};
  justify-content: ${({ theme }) => (!theme.sizes.isComputer ? 'flex-start' : 'space-between')};
  align-items: 'flex-start';
  margin-bottom: ${({ sectionVisible }) => (sectionVisible ? '10px' : '0')};
  margin-right: 5px;
`;

const SectionHeader = ({
  title, info, sectionVisible, marginBottom,
}) => (
  <div style={{ marginLeft: 3, marginBottom: (sectionVisible || marginBottom) ? 10 : 0 }}>
    <h3 style={{ marginBottom: 3 }}>{title}</h3>
    <p>{info}</p>
  </div>
);

const templateFieldTypes = [
  {
    key: 'row', name: 'Row divider', icon: 'arrows alternate vertical', desc: 'Add a row divider',
  },
  {
    key: 'video', name: 'Video', icon: 'film', desc: 'Upload a video or embed from YouTube or Vimeo.',
  },
  {
    key: 'image', name: 'Image', icon: 'camera', desc: 'Upload an image or find one online.',
  },
  {
    key: 'multiimage', name: 'Multi-Image', icon: 'images', desc: 'Upload a gallery of images in a single field',
  },
  {
    key: 'file', name: 'File', icon: 'file', desc: 'Upload a file or document.',
  },
  {
    key: 'shorttext', name: 'Short text (one line)', icon: 'font', desc: 'Single line of text without formatting.',
  },
  {
    key: 'longtext', name: 'Long text (paragraphs)', icon: 'left align', desc: 'Multiple lines/paragraphs of text without formatting.',
  },
  {
    key: 'richtext', name: 'Formatted text, images, videos', icon: 'paragraph', desc: 'Allow users free-reign with rich text editing along with image and video uploading.',
  },
  {
    key: 'slider', name: 'Slider', icon: 'sliders horizontal', desc: 'Numerical slider (with justification).',
  },
  {
    key: 'dropdown', name: 'Dropdown box', icon: 'list', desc: 'Option selection from a dropdown.',
  },
  {
    key: 'optionMultiple', name: 'Multiple selection', icon: 'check square outline', desc: 'Multiple selection from checkboxes.',
  },
  {
    key: 'creditsafereport', name: 'Creditsafe report', icon: 'file text', desc: 'Verify and generate a credit report for a company.',
  },
  {
    key: 'infotext', name: 'Information text', icon: 'info circle', desc: 'Provide the idea creator with information to aid their idea. You can include images and links. This field takes no input from the idea creator, and is only used for display.',
  },
];

const FormatSection = (props) => {
  const {
    body = [],
    section,
    index: sectionIndex,
    deleteSection,
    editSectionInclude,
    editSectionName,
    editSectionIsRequired,
    t,
    assessmentEnabled,
    editSectionAssessment,
    allowCreditsafeReport,
    editSectionField,
    moveSection,
    challengeId,
    sixCsEnabled,
    setRef,
  } = props;
  const [sectionVisible, setSectionVisible] = useState(true);
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);

  const editAssessmentScore = useCallback((e) => {
    util.limitNumberInput(e, (numericalValue) => editSectionAssessment(sectionIndex, 'availableScore', numericalValue || 0));
  }, [editSectionAssessment, sectionIndex]);

  const editAssessmentWeighting = useCallback((e) => {
    util.limitNumberInput(e, (numericalValue) => editSectionAssessment(sectionIndex, 'weighting', numericalValue || 0));
  }, [editSectionAssessment, sectionIndex]);

  const editAssessmentNotes = useCallback((e) => {
    editSectionAssessment(sectionIndex, 'notes', e.target.value);
  }, [editSectionAssessment, sectionIndex]);

  const addFieldAtIndex = useCallback((type, index) => {
    if (type === 'creditsafereport' && !allowCreditsafeReport) {
      toast.error('The Creditsafe integration is not yet configured for your organisation.');
      return;
    }
    const fields = Object.assign([], section.fields || []);
    const newField = {
      title: 'My new field', description: 'Field description', type, width: 'sixteen', id: uuid.v4(),
    };
    if (index < 1) {
      fields.unshift(newField);
      editSectionField(sectionIndex, fields);
    } else if (index > (fields.length - 1)) {
      fields.push(newField);
      editSectionField(sectionIndex, fields);
    } else {
      const newFields = [
        ...fields.slice(0, index),
        newField,
        ...fields.slice(index),
      ];
      editSectionField(sectionIndex, newFields);
    }
  }, [allowCreditsafeReport, editSectionField, sectionIndex, section]);

  const addNewField = useCallback((type) => addFieldAtIndex(type, (section.fields || []).length), [section.fields, addFieldAtIndex]);

  const addNewFieldBefore = useCallback((type, index) => {
    addFieldAtIndex(type, index);
  }, [addFieldAtIndex]);

  const addNewFieldAfter = useCallback((type, index) => {
    addFieldAtIndex(type, index + 1);
  }, [addFieldAtIndex]);

  const editFieldTitle = useCallback((fieldIndex, title) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) field.title = title;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldSliderMin = useCallback((fieldIndex, min) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field && !Number.isNaN(min) && ((field.maxslider && field.maxslider > min) || (!field.maxslider && (min <= 9)))) field.minslider = min;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldSliderMax = useCallback((fieldIndex, max) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field && !Number.isNaN(max) && (field.minslider < max)) field.maxslider = max;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldMaxImages = useCallback((fieldIndex, max) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field && !Number.isNaN(max)) field.maxImages = max;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldLimit = useCallback((fieldIndex, limit) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field && !Number.isNaN(limit)) {
      delete field.characterLimit;
      field.wordLimit = limit;
    }
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const removeCharacterLimit = useCallback((fieldIndex) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) {
      delete field.characterLimit;
    }
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editInfoTextContent = useCallback((fieldIndex, content) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field.type !== 'infotext') return;
    field.content = content;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldDescription = useCallback((fieldIndex, description) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) field.description = description;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldTextbox = useCallback((fieldIndex, hidden) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) field.hideTextbox = hidden;
    if (hidden) {
      field.required = undefined;
      field.characterLimit = undefined;
    }
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldSlider = useCallback((fieldIndex, hidden) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) field.hideSliders = hidden;
    field.maxslider = null;
    field.minslider = null;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldOptions = useCallback((fieldIndex, options) => {
    const fields = Object.assign([], section.fields);
    fields[fieldIndex] = { ...fields[fieldIndex], options };
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldWidth = useCallback((fieldIndex, width) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) field.width = width;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const editFieldRequired = useCallback((fieldIndex, required) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    if (field) field.required = required;
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const moveField = useCallback((fieldIndex, direction) => {
    const fields = Object.assign([], section.fields);
    const field = fields[fieldIndex];
    fields.splice(fieldIndex, 1);
    if (direction === 'up') fields.splice(fieldIndex - 1, 0, field);
    else fields.splice(fieldIndex + 1, 0, field);
    editSectionField(sectionIndex, fields);
  }, [section, sectionIndex, editSectionField]);

  const deleteField = useCallback((fieldIndex) => {
    util.confirm(
      'Delete this field?',
      'Deleting a field will make the data held by the field in ideas to this challenge unavailable.',
    )
      .then(() => {
        const fields = section.fields.map((f) => f);
        fields.splice(fieldIndex, 1);
        editSectionField(sectionIndex, fields);
      }).catch(() => { });
  }, [section, sectionIndex, editSectionField]);

  const includeSection = section?.include ? includeSections[section.include] : {};

  const bodyHasBusinessProfileSection = useMemo(() => {
    return !!body.find((s) => s.type === 'businessProfile');
  }, [body]);

  if (!section) return null;
  return (
    <Draggable key={sectionIndex} draggableId={`dragSection-${sectionIndex}`} index={sectionIndex}>
      {(provided) => (
        <StyledContainer section={section} includeSection={includeSection} {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
          <StyledSectionHeader sectionVisible={sectionVisible} ref={(ref) => setRef(ref, section.id)} id={section.id}>
            {sectionVisible
              ? (
                <Form>
                  <Form.Group widths='equal'>
                    <Form.Select
                      label='Section type'
                      value={section.include || section.type || 'custom'} id={`section-${sectionIndex}`}
                      options={[
                        { key: 'custom', value: 'custom', text: t('ideas.6Cs.sections.section', { section: 'custom' }) },
                        { key: 'edi', value: 'edi', text: 'EDI section' },
                        user?.organisation?.enabledFeatures?.includes('ideaBusinessProfiles')
                          ? { key: 'businessProfile', value: 'businessProfile', text: 'Organisation Profile', disabled: bodyHasBusinessProfileSection }
                          :  null,
                        ...(
                          sixCsEnabled
                            ? [
                              { key: 'concept', value: 'concept', text: t('ideas.6Cs.sections.section', { section: 'concept' }) },
                              { key: 'customers', value: 'customers', text: t('ideas.6Cs.sections.section', { section: 'customers' }) },
                              { key: 'competitors', value: 'competitors', text: t('ideas.6Cs.sections.section', { section: 'competitors' }) },
                              { key: 'compatibility', value: 'compatibility', text: t('ideas.6Cs.sections.section', { section: 'compatibility' }) },
                              { key: 'contents', value: 'contents', text: t('ideas.6Cs.sections.section', { section: 'contents' }) },
                              { key: 'cash', value: 'cash', text: t('ideas.6Cs.sections.section', { section: 'cash' }) },
                            ] : []
                        )
                      ].filter((item) => Boolean(item))}
                      onChange={(e, s) => editSectionInclude(sectionIndex, s.value)}
                    />

                    {!section.include
                      && <Form.Input label='Section name' type='text' placeholder={t('challenge.format.formatSection.name')}
                        value={section.name}
                        onChange={(e) => editSectionName(sectionIndex, e.target.value)}
                      />
                    }
                  </Form.Group>
                </Form>
              ) : (
                <div>
                  {section.include
                    ? (
                      <h3>
                        {t('ideas.6Cs.sections.section', { section: section.include })}
                      </h3>
                    ) : (
                      <h3>{section.name}{section.fields ? ` - ${section.fields.length} fields` : ' - 0 fields'}</h3>
                    )
                  }
                </div>
              )
            }
            <div
              style={{
                display: 'flex', flexDirection: 'row', marginBottom: !theme.sizes.isComputer ? 10 : null, height: theme.sizes.isComputer ? 20 : null,
              }}
            >
              <div
                style={{
                  borderRight: '1px solid lightgray',
                  paddingRight: 10,
                  marginRight: 10,
                }}
              >
                <FakeLink
                  withBorder
                  style={{ color: '#000' }}
                  onClick={() => setSectionVisible((prevVisible) => !prevVisible)}
                >
                  <Icon name={sectionVisible ? 'chevron up' : 'chevron down'} />
                  {sectionVisible ? 'Minimise' : 'Maximise'}
                </FakeLink>
              </div>
              <Dropdown pointing icon={null}
                trigger={
                  <FakeLink withBorder style={{ color: '#000' }}><Icon name='dropdown' />Section options </FakeLink>
                }
              >
                <Dropdown.Menu>
                  {sectionIndex > 0
                    && <Dropdown.Item icon='arrow up' content={t('challenge.format.formatSection.options.moveUp')} onClick={() => moveSection(sectionIndex, 'up')} />
                  }
                  {sectionIndex < (body.length - 1)
                    && <Dropdown.Item icon='arrow down' content={t('challenge.format.formatSection.options.moveDown')} onClick={() => moveSection(sectionIndex, 'down')} />
                  }
                  {((sectionIndex > 0) || (sectionIndex < (body.length - 1)))
                    && <Dropdown.Divider />
                  }
                  <Dropdown.Item icon='trash' content={t('challenge.format.formatSection.options.delete')} onClick={() => deleteSection(sectionIndex)} />
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </StyledSectionHeader>

          {section?.type === 'edi'
            ? (<Message info>
              <Message.Header><Icon name='lock' /> This section is about equality, diversity, and inclusion</Message.Header>
              <Message.Content>Completed fields in this section will only be visible to users with the "View EDI" permission.</Message.Content>
            </Message>)
            : null
          }

          {section?.type === 'businessProfile'
            ? (
              <>
                <Message info>
                  <Message.Header>Organisation Profile</Message.Header>
                  <Message.Content>The idea organisation profile section allows idea-creators to use a wizard to provide their organisation profile information. They can provide details automatically using Crunchbase, or enter it manually. Only one of these sections can be used in the format.</Message.Content>
                </Message>
                <CheckboxHeader
                  header="Require organisation profiles"
                  as='h5'
                  plain
                  checked={section.isRequired}
                  onChange={(on) => editSectionIsRequired(sectionIndex, on)}
                >
                  Mark organisation profiles as a mandatory field. Users will only be able to submit ideas once they have attached a valid organisation profile.
                </CheckboxHeader>
              </>
            ) : null
          }

          {sectionVisible
            && (
              <>
                {assessmentEnabled
                  && (
                    <>
                      <SectionHeader title="Section assessment" info="If relevant, indicate how this section should be assessed." marginBottom />
                      <StyledSection>
                        <Form>
                          <Form.Group widths='equal'>
                            <Form.Input placeholder='10' label='Total score available for section' value={section.assessment?.availableScore} onChange={editAssessmentScore} />
                            <Form.Input placeholder='20' label='Section score weighting (%)' value={section.assessment?.weighting} onChange={editAssessmentWeighting} />
                            <Form.TextArea label='Notes to assessors' placeholder='Write assessment criteria or instructions to assessors...' value={section.assessment?.notes || ''} onChange={editAssessmentNotes} rows={1} />
                          </Form.Group>
                        </Form>
                      </StyledSection>
                    </>
                  )
                }

                {section.type !== 'businessProfile'
                  ? (
                    <SectionHeader title={section.include ? t(`ideas.6Cs.sections.${section.include}.name`) : 'Section fields'} info={section.include ? t(`ideas.6Cs.sections.${section.include}.description`) : 'The fields (questions) users will complete in this section. These can include text inputs, image and video uploads, and more.'} sectionVisible />
                  ) : null
                }

                {section.include
                  && (
                    <Message info size='small'>
                      The fields in this type of section cannot be modified. If you wish to customise the fields your users can edit please choose the "Custom" section type.
                    </Message>
                  )
                }

                {!section.include && section.type !== 'businessProfile'
                  && <>
                    <Droppable
                      type='field'
                      droppableId={`section-${sectionIndex}`}
                    >
                      {(includeProvided) => (
                        <div ref={includeProvided.innerRef} {...includeProvided.droppableProps}>
                          <Grid stackable centered>
                            {section.fields && section.fields.map((field, i) => (
                              <FormatField
                                key={i}
                                field={field}
                                fields={section.fields}
                                index={i}
                                editFieldTitle={editFieldTitle}
                                editFieldDescription={editFieldDescription}
                                editFieldLimit={editFieldLimit}
                                removeCharacterLimit={removeCharacterLimit}
                                editFieldSliderMin={editFieldSliderMin}
                                editFieldSliderMax={editFieldSliderMax}
                                editFieldMaxImages={editFieldMaxImages}
                                editFieldSlider={editFieldSlider}
                                editFieldTextbox={editFieldTextbox}
                                editFieldOptions={editFieldOptions}
                                editFieldWidth={editFieldWidth}
                                editInfoTextContent={editInfoTextContent}
                                addNewFieldBefore={addNewFieldBefore}
                                addNewFieldAfter={addNewFieldAfter}
                                moveField={moveField}
                                deleteField={deleteField}
                                editFieldRequired={editFieldRequired}
                                fieldType={templateFieldTypes.find((f) => f.key === field.type)}
                                templateFieldTypes={templateFieldTypes}
                                challengeId={challengeId}
                                setRef={setRef}
                              />
                            ))}
                          </Grid>
                          {includeProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                    <Dropdown style={{
                      width: '100%', marginTop: 10, marginBottom: 10, display: 'flex', justifyContent: 'center',
                    }} icon={null} pointing
                      trigger={<Button fluid color='teal' icon='plus' content={t('challenge.format.formatSection.new.title')} style={{ marginTop: '20px', width: '100%' }} />}
                    >
                      <Dropdown.Menu style={{ left: 'auto', right: 'auto' }}>
                        {templateFieldTypes.map((type) => <Dropdown.Item key={type.key} data-tooltip={type.desc} icon={type.icon} content={type.name} onClick={() => addNewField(type.key)} />)}
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                }
              </>
            )
          }
        </StyledContainer>
      )}
    </Draggable>
  );
};

export default withTranslation()(FormatSection);
